import moment from 'moment';
import { Dose, IVaccineStatus, VACCINATION_STATUS } from '../api/fetchVaccineSchedule';
import { Fragment } from 'react';
import { Checkbox, Divider } from 'antd';
import { buttons } from './VaccineSelectionModal';
import { getDigitSuffix } from '../store/utils';

export const DoseCard: React.FC<{
    c: Dose;
    currVal: Dose[];
    hrsFromBirth?: string | undefined;
    selectedVaccines: Dose[] | undefined;
    setSelectedVaccines: (vac: Dose[]) => void;
    setShowModal: (val: {
        isVisible: boolean;
        status?: VACCINATION_STATUS.GIVEN | VACCINATION_STATUS.DUE;
        date?: string;
    }) => void;
    administedVaccines: IVaccineStatus[];
    idx: number;
    dob: moment.Moment;
    today: moment.Moment;
    currSch: 'plan' | 'otherVaccs';
}> = ({
    c,
    hrsFromBirth,
    selectedVaccines,
    setSelectedVaccines,
    setShowModal,
    administedVaccines,
    idx,
    currVal,
    dob,
    today,
    currSch,
}) => {
    const vaccineDate = hrsFromBirth ? dob.clone().add(parseInt(hrsFromBirth), 'hours') : undefined;

    const vaccineStat = administedVaccines.find((vac) => vac.id === c.vaccineId);
    const isSelected = selectedVaccines?.find((v) => v.vaccineId === c.vaccineId);

    return (
        <Fragment>
            <div
                className={`flex cursor-pointer h-full justify-center flex-col ${
                    currVal.length > 1
                        ? currSch === 'plan'
                            ? 'p-16'
                            : 'py-24 px-16'
                        : 'px-16 py-12'
                } gap-8 dose-box-${idx} ${
                    vaccineStat?.status === VACCINATION_STATUS.DUE
                        ? 'bg-background-light-yellow'
                        : vaccineStat?.status === VACCINATION_STATUS.GIVEN
                        ? 'bg-darwin-green-bg-dark'
                        : vaccineStat?.status === VACCINATION_STATUS.MISSED ||
                          vaccineStat?.status === VACCINATION_STATUS.PATIENT_REFUSED
                        ? 'bg-background-light-red'
                        : ''
                } ${!vaccineStat?.status ? 'hover:bg-darwin-accent-bright-blue-100' : ''}`}
                onClick={() => {
                    setSelectedVaccines([{ ...c, status: vaccineStat }]);
                    setShowModal({
                        isVisible: true,
                        status: undefined,
                        date: vaccineStat?.date ?? today.toISOString(),
                    });
                }}
            >
                <div className={`flex items-center h-26 min-h-26 justify-between`}>
                    <p className="font-700 text-14">{c.vaccineName}</p>

                    <Checkbox
                        onChange={(e) => {
                            if (e.target.checked === true) {
                                setSelectedVaccines([
                                    ...(selectedVaccines ?? []),
                                    { ...c, status: vaccineStat },
                                ]);
                            } else {
                                setSelectedVaccines(
                                    (selectedVaccines ?? []).filter(
                                        (v) => v.vaccineId !== c?.vaccineId,
                                    ),
                                );
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        checked={isSelected ? true : false}
                        className={isSelected ? '' : ` dose-box-child-element-${idx}`}
                    />
                </div>

                <DoseAdministrationInfo
                    hrsFromBirth={hrsFromBirth}
                    vaccineDate={vaccineDate}
                    vaccineStat={vaccineStat}
                    dob={dob}
                    currSch={currSch}
                />
            </div>
            {idx !== currVal.length - 1 && <Divider className="m-0" />}
            <style>
                {`
                    .dose-box-child-element-${idx} {
                        display: none;
                    }
    
                    .dose-box-${idx}:hover .dose-box-child-element-${idx} {
                        display: flex;
                    }
                `}
            </style>
        </Fragment>
    );
};

const DoseAdministrationInfo: React.FC<{
    vaccineStat: IVaccineStatus | undefined;
    hrsFromBirth: string | undefined;
    vaccineDate: moment.Moment | undefined;
    dob: moment.Moment;
    currSch: 'plan' | 'otherVaccs';
}> = ({ hrsFromBirth, vaccineStat, vaccineDate, dob, currSch }) => {
    const vaccDateInweeks = moment(vaccineStat?.date).diff(dob, 'weeks');

    return (
        <Fragment>
            {vaccineStat?.status === VACCINATION_STATUS.DUE ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-8">
                        {buttons.find((btn) => btn.text === VACCINATION_STATUS.DUE)?.icon}
                        {vaccineStat.date ? (
                            <p className="font-400 text-13">
                                {currSch !== 'otherVaccs' &&
                                    (vaccDateInweeks > 0
                                        ? `${vaccDateInweeks}${getDigitSuffix(
                                              vaccDateInweeks,
                                          )} Week, `
                                        : 'Birth, ')}
                                {moment(vaccineStat.date).format("DD MMM'YY")}
                            </p>
                        ) : (
                            <p className="font-400 text-13">Due</p>
                        )}
                    </div>
                    {vaccineStat.brand?.brandName && (
                        <p className="font-400 text-12">{vaccineStat.brand?.brandName ?? ''}</p>
                    )}
                </div>
            ) : vaccineStat?.status === VACCINATION_STATUS.GIVEN ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-8">
                        {buttons.find((btn) => btn.text === VACCINATION_STATUS.GIVEN)?.icon}
                        {vaccineStat.date ? (
                            <p className="font-400 text-13">
                                {currSch !== 'otherVaccs' &&
                                    (vaccDateInweeks > 0
                                        ? `${vaccDateInweeks}${getDigitSuffix(
                                              vaccDateInweeks,
                                          )} Week, `
                                        : 'Birth, ')}
                                {moment(vaccineStat.date).format("DD MMM'YY")}
                            </p>
                        ) : (
                            <p className="font-400 text-13">Given</p>
                        )}
                    </div>
                    {vaccineStat.brand?.brandName && (
                        <p className="font-400 text-12">{vaccineStat.brand?.brandName ?? ''}</p>
                    )}
                </div>
            ) : vaccineStat?.status === VACCINATION_STATUS.PATIENT_REFUSED ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-8">
                        {
                            buttons.find((btn) => btn.text === VACCINATION_STATUS.PATIENT_REFUSED)
                                ?.icon
                        }
                        <p className="font-400 text-13">
                            Patient refused{' '}
                            {vaccineStat.date
                                ? `on ${moment(vaccineStat.date).format("DD MMM'YY")}`
                                : ''}
                        </p>
                    </div>
                    {vaccineStat.brand?.brandName && (
                        <p className="font-400 text-12">{vaccineStat.brand?.brandName ?? ''}</p>
                    )}
                </div>
            ) : vaccineStat?.status === VACCINATION_STATUS.MISSED ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-8">
                        {buttons.find((btn) => btn.text === VACCINATION_STATUS.MISSED)?.icon}
                        <p className="font-400 text-13">Missed</p>
                    </div>
                    {vaccineStat.brand?.brandName && (
                        <p className="font-400 text-12">{vaccineStat.brand?.brandName ?? ''}</p>
                    )}
                </div>
            ) : vaccineDate && hrsFromBirth && parseInt(hrsFromBirth) >= 0 && currSch === 'plan' ? (
                <div className="flex items-center justify-between">
                    <p className="font-400 text-13">{vaccineDate.format("DD MMM'YY")}</p>
                </div>
            ) : null}
        </Fragment>
    );
};

const DoseCardWrapper: React.FC<{
    currVal: Dose[];
    hrsFromBirth?: string | undefined;
    selectedVaccines: Dose[] | undefined;
    setSelectedVaccines: (vac: Dose[]) => void;
    setShowModal: (val: {
        isVisible: boolean;
        status?: VACCINATION_STATUS.GIVEN | VACCINATION_STATUS.DUE;
        date?: string;
    }) => void;
    administedVaccines: IVaccineStatus[];
    dob: moment.Moment;
    today: moment.Moment;
    currSch: 'plan' | 'otherVaccs';
}> = ({
    currVal,
    hrsFromBirth,
    selectedVaccines,
    setSelectedVaccines,
    setShowModal,
    administedVaccines,
    dob,
    today,
    currSch,
}) => {
    return currVal && currVal.length !== 0 ? (
        <Fragment>
            <div className="flex flex-col w-full h-full justify-center">
                {currVal.map((c, idx) => (
                    <DoseCard
                        administedVaccines={administedVaccines}
                        c={c}
                        currVal={currVal}
                        idx={idx}
                        selectedVaccines={selectedVaccines}
                        setSelectedVaccines={setSelectedVaccines}
                        setShowModal={setShowModal}
                        hrsFromBirth={hrsFromBirth}
                        dob={dob}
                        today={today}
                        currSch={currSch}
                    />
                ))}
            </div>
        </Fragment>
    ) : null;
};

export default DoseCardWrapper;
